import * as THREE from 'three';
import { DIRECTIONS, axisIndex, createRng, cross, doorKey, type Maze, type Vec3 } from '@qoobd/rules';
import { ROOM_SIZE } from '../game/controller';

const H = ROOM_SIZE / 2;
/** Half-width of a door opening. */
const D = 0.85;
/** Half-thickness of a wall (door jambs span this depth). */
const J = 0.18;

export type RoomLayer = 'wall' | 'jamb' | 'trail' | 'idle' | 'bars' | 'oneway';
export const ROOM_LAYERS: readonly RoomLayer[] = ['wall', 'jamb', 'trail', 'idle', 'bars', 'oneway'];

interface Buffers {
  p: number[];
  n: number[];
  u: number[];
  c: number[];
}

const point = (c: Vec3, n: Vec3, t1: Vec3, t2: Vec3, a: number, b: number, o: number): Vec3 => {
  const h = H + o;
  return [
    c[0] + n[0] * h + t1[0] * a + t2[0] * b,
    c[1] + n[1] * h + t1[1] * a + t2[1] * b,
    c[2] + n[2] * h + t1[2] * a + t2[2] * b,
  ];
};

function quad(g: Buffers, ps: [Vec3, Vec3, Vec3, Vec3], uv: [number, number][], shade: number): void {
  const [p0, p1, p2] = ps;
  const ax = p1[0] - p0[0], ay = p1[1] - p0[1], az = p1[2] - p0[2];
  const bx = p2[0] - p0[0], by = p2[1] - p0[1], bz = p2[2] - p0[2];
  let nx = ay * bz - az * by, ny = az * bx - ax * bz, nz = ax * by - ay * bx;
  const l = Math.hypot(nx, ny, nz) || 1;
  nx /= l; ny /= l; nz /= l;
  for (const k of [0, 1, 2, 0, 2, 3]) {
    const p = ps[k]!;
    g.p.push(p[0], p[1], p[2]);
    g.n.push(nx, ny, nz);
    g.u.push(uv[k]![0], uv[k]![1]);
    g.c.push(shade, shade, shade);
  }
}

function rect(g: Buffers, c: Vec3, n: Vec3, t1: Vec3, t2: Vec3, a0: number, a1: number, b0: number, b1: number, o: number, shade = 1): void {
  const uv = (a: number, b: number): [number, number] => [(a + H) / ROOM_SIZE, (b + H) / ROOM_SIZE];
  quad(
    g,
    [point(c, n, t1, t2, a0, b0, o), point(c, n, t1, t2, a1, b0, o), point(c, n, t1, t2, a1, b1, o), point(c, n, t1, t2, a0, b1, o)],
    [uv(a0, b0), uv(a1, b0), uv(a1, b1), uv(a0, b1)],
    shade,
  );
}

/**
 * Builds merged geometry for the given rooms: thick opaque walls, door openings with jambs,
 * door-frame glow (amber where the player has passed) and 1/2/3-bar axis markers.
 */
export function buildRooms(maze: Maze, rooms: Iterable<number>, trail: ReadonlySet<number>): Record<RoomLayer, THREE.BufferGeometry> {
  const g: Record<RoomLayer, Buffers> = {
    wall: { p: [], n: [], u: [], c: [] },
    jamb: { p: [], n: [], u: [], c: [] },
    trail: { p: [], n: [], u: [], c: [] },
    idle: { p: [], n: [], u: [], c: [] },
    bars: { p: [], n: [], u: [], c: [] },
    oneway: { p: [], n: [], u: [], c: [] },
  };
  const done = new Set<string>();
  for (const room of rooms) {
    const [x, y, z] = maze.coords(room);
    const c: Vec3 = [x * ROOM_SIZE, y * ROOM_SIZE, z * ROOM_SIZE];
    DIRECTIONS.forEach((d, k) => {
      const next = maze.neighbor(room, d);
      const key = next >= 0 ? `d${doorKey(room, next)}` : `b${room}_${k}`;
      if (done.has(key)) return;
      done.add(key);
      const ax = axisIndex(d);
      const t1: [number, number, number] = [0, 0, 0];
      const t2: [number, number, number] = [0, 0, 0];
      t1[(ax + 1) % 3] = 1;
      t2[(ax + 2) % 3] = 1;
      const shade = 0.82 + (((room * 131 + k * 71) % 97) / 97) * 0.22; // per-wall variation
      const open = next >= 0 && maze.hasDoor(room, next);
      for (const o of [-J, J]) {
        if (!open) {
          rect(g.wall, c, d, t1, t2, -H, H, -H, H, o, shade);
          continue;
        }
        rect(g.wall, c, d, t1, t2, -H, -D, -H, H, o, shade);
        rect(g.wall, c, d, t1, t2, D, H, -H, H, o, shade);
        rect(g.wall, c, d, t1, t2, -D, D, -H, -D, o, shade);
        rect(g.wall, c, d, t1, t2, -D, D, D, H, o, shade);
        const w = 0.065;
        const oo = o + (o > 0 ? 0.01 : -0.01);
        const glow = trail.has(doorKey(room, next)) ? g.trail : g.idle;
        rect(glow, c, d, t1, t2, -D - w, D + w, D, D + w, oo);
        rect(glow, c, d, t1, t2, -D - w, D + w, -D - w, -D, oo);
        rect(glow, c, d, t1, t2, -D - w, -D, -D, D, oo);
        rect(glow, c, d, t1, t2, D, D + w, -D, D, oo);
        for (let b = 0; b <= ax; b++) {
          const a0 = D + 0.26 + b * 0.17;
          rect(g.bars, c, d, t1, t2, a0, a0 + 0.08, -0.22, 0.22, oo);
        }
      }
      // One-way door: a translucent red shutter, drawn single-sided so it's seen only from the
      // side you can't pass from. From the passable side the doorway looks ordinary.
      const from = open ? maze.oneWayFrom(room, next) : null;
      if (from !== null) {
        const blockedSide = from === room ? 1 : -1; // passing room → next is allowed, so `next` (along +d) is the blocked side
        const P = (a: number, b: number) => point(c, d, t1, t2, a, b, 0);
        const corners: [Vec3, Vec3, Vec3, Vec3] = [P(-D, -D), P(D, -D), P(D, D), P(-D, D)];
        // quad() takes its normal from the winding; flip it to face the blocked room.
        const n = cross(t1, t2);
        const facesD = n[0] * d[0] + n[1] * d[1] + n[2] * d[2] > 0;
        const wantD = blockedSide > 0;
        const ordered: [Vec3, Vec3, Vec3, Vec3] = facesD === wantD ? corners : [corners[0], corners[3], corners[2], corners[1]];
        const uv: [number, number][] = [[0, 0], [1, 0], [1, 1], [0, 1]];
        quad(g.oneway, ordered, uv, 1);
      }
      if (open) {
        const uv: [number, number][] = [[0, 0], [1, 0], [1, 0.1], [0, 0.1]];
        const P = (a: number, b: number, o: number) => point(c, d, t1, t2, a, b, o);
        quad(g.jamb, [P(-D, -D, -J), P(-D, D, -J), P(-D, D, J), P(-D, -D, J)], uv, 1);
        quad(g.jamb, [P(D, -D, -J), P(D, D, -J), P(D, D, J), P(D, -D, J)], uv, 1);
        quad(g.jamb, [P(-D, -D, -J), P(D, -D, -J), P(D, -D, J), P(-D, -D, J)], uv, 1);
        quad(g.jamb, [P(-D, D, -J), P(D, D, -J), P(D, D, J), P(-D, D, J)], uv, 1);
      }
    });
  }
  const out = {} as Record<RoomLayer, THREE.BufferGeometry>;
  for (const layer of ROOM_LAYERS) {
    const b = g[layer];
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(b.p, 3));
    geo.setAttribute('normal', new THREE.Float32BufferAttribute(b.n, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(b.u, 2));
    geo.setAttribute('color', new THREE.Float32BufferAttribute(b.c, 3));
    out[layer] = geo;
  }
  return out;
}

/**
 * Procedural gunmetal panel texture (a stand-in for the CC0 PBR texture sets the brief calls for).
 */
export function panelTexture(anisotropy: number): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 512;
  const g = canvas.getContext('2d')!;
  const r = createRng(7);
  g.fillStyle = '#4a4f57';
  g.fillRect(0, 0, 512, 512);
  for (let n = 0; n < 9000; n++) {
    const v = Math.floor(60 + r() * 40);
    g.fillStyle = `rgba(${v},${v + 3},${v + 8},${0.08 + r() * 0.12})`;
    g.fillRect(r() * 512, r() * 512, 1 + r() * 3, 1 + r() * 3);
  }
  for (let s = 0; s < 40; s++) {
    g.strokeStyle = `rgba(200,200,205,${0.03 + r() * 0.05})`;
    g.beginPath();
    const x = r() * 512, y = r() * 512;
    g.moveTo(x, y);
    g.lineTo(x + (r() - 0.5) * 80, y + (r() - 0.5) * 20);
    g.stroke();
  }
  for (const p of [0, 128, 256, 384, 512]) {
    g.fillStyle = '#1b1e23';
    g.fillRect(p - 2, 0, 4, 512);
    g.fillRect(0, p - 2, 512, 4);
    g.fillStyle = 'rgba(255,255,255,.08)';
    g.fillRect(p + 2, 0, 1, 512);
    g.fillRect(0, p + 2, 512, 1);
  }
  g.fillStyle = '#2a2e34';
  g.fillRect(128, 248, 256, 16);
  for (let a = 0; a < 4; a++)
    for (let b = 0; b < 4; b++)
      for (const [ox, oy] of [[14, 14], [114, 14], [14, 114], [114, 114]] as const) {
        const x = a * 128 + ox, y = b * 128 + oy;
        g.fillStyle = '#2c3036';
        g.beginPath();
        g.arc(x, y, 4, 0, Math.PI * 2);
        g.fill();
        g.fillStyle = 'rgba(255,255,255,.18)';
        g.beginPath();
        g.arc(x - 1, y - 1, 1.5, 0, Math.PI * 2);
        g.fill();
      }
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = anisotropy;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

/** Louvred red shutter for the blocked side of a one-way door. */
export function shutterTexture(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 128;
  const g = canvas.getContext('2d')!;
  g.fillStyle = 'rgba(229,72,77,0.18)';
  g.fillRect(0, 0, 128, 128);
  for (let y = 6; y < 128; y += 14) {
    g.fillStyle = 'rgba(229,72,77,0.55)';
    g.fillRect(6, y, 116, 5);
  }
  g.strokeStyle = 'rgba(229,72,77,0.9)';
  g.lineWidth = 4;
  g.strokeRect(2, 2, 124, 124);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
